import { Box } from '@mui/material'
import { FC, useContext } from 'react'

// others
import { EntryStatus, useDeleteEntryMutation } from '../../../generated'
import { NOT_APPLICABLE } from '../../constants'
import { AppContext, AuthContext } from '../../context'
import { ActionType } from '../../context/AppContextReducer'

// components
import { ManualTimeEntryForm } from '../../interfaceTypes/forms'
import { TimeEntryMenuProps } from '../../interfaceTypes/props'
import { Alert } from '../common/Alert'
import CustomMenu from '../common/CustomMenu'
import { DeleteModalContent } from '../common/DeleteModalContent'
import { EntryActionType } from './EntryReducer'
import ManualEntry from './ManualEntry'

export const EntryMenu: FC<TimeEntryMenuProps> = ({ currentTimeEntry, entryDispatch, refetch, lockCase }): JSX.Element => {
  const { isAdmin } = useContext(AuthContext)
  const { dispatch, isExpenseEntry, isLegalFee } = useContext(AppContext)

  const { id, status } = currentTimeEntry || {}

  const [deleteTimeEntry, { loading: deleting }] = useDeleteEntryMutation({
    onError: error => {
      Alert.error(`Entry couldn't be deleted due to: ${error.message}`)
    },
    onCompleted: async () => {
      dispatch({ type: ActionType.SET_IS_DELETE_OPEN, isDeleteOpen: false })
      Alert.success('Entry deleted successfully')
      await refetch?.()
    }
  })

  const deleteEntry = async (entryId: string) => await deleteTimeEntry({ variables: { entryId } })

  return (
    <CustomMenu
      id={id}
      menuTitle="Entry"
      onDelete={deleteEntry}
      handleEdit={() => entryDispatch({ type: EntryActionType.SET_CURRENT_ENTRY, currentEntry: currentTimeEntry })}
      hideDelete={status === EntryStatus.Draft || status === EntryStatus.Billed}
      deleteContent={
        <Box mt={1}>
          {isAdmin && (
            <DeleteModalContent
              label="User"
              value={
                `${currentTimeEntry?.user?.firstName ?? ''} ${currentTimeEntry?.user?.lastName ?? ''}`.trim() ||
                NOT_APPLICABLE
              }
            />
          )}{' '}
          <DeleteModalContent label="Case" value={currentTimeEntry?.case?.title || NOT_APPLICABLE} />
          <DeleteModalContent label="Description" value={currentTimeEntry?.description || NOT_APPLICABLE} />
          <DeleteModalContent
            label={isExpenseEntry ? 'Category' : isLegalFee ? 'Fee Type' : 'Duration'}
            value={
              isExpenseEntry
                ? currentTimeEntry?.category?.name || ''
                : isLegalFee
                ? currentTimeEntry.feeType?.feeType || ''
                : currentTimeEntry?.duration || ''
            }
          />
        </Box>
      }
      deleting={deleting}
      modal={
        <ManualEntry
          title={`Edit ${isExpenseEntry ? 'Expense' : ''} Entry`}
          time={currentTimeEntry.duration ?? ''}
          defaultValues={
            {
              entryId: currentTimeEntry?.id || '',
              ...currentTimeEntry,
              // Seed the matter number so the rate lookup searches for THIS case
              // specifically — the user's full matter list is paginated and may
              // not contain it on the first page, which would zero out the rate.
              caseNumber: currentTimeEntry?.case?.caseNumber ?? currentTimeEntry?.case?.consultMatterNumber ?? undefined
            } as ManualTimeEntryForm
          }
          initialCase={currentTimeEntry?.case}
          lockCase={lockCase}
          refetch={refetch}
        />
      }
    />
  )
}
